import { useMemo } from "react";
import { useIntl } from "react-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { extractDataFromFlightFare } from "@/features/flights/components/flight-fares/FlightFareCard/extractDataFromFlightFare";
import { FlightFareBaggageInfo } from "@/features/flights/components/flight-fares/FlightFareCard/sections/FlightFareBaggageInfo";
import { FlightFareChangeRefundInfo } from "@/features/flights/components/flight-fares/FlightFareCard/sections/FlightFareChangeRefundInfo";
import { FlightFareOtherBenefitsInfo } from "@/features/flights/components/flight-fares/FlightFareCard/sections/FlightFareOtherBenefitsInfo";
import {
  getArrivalLegFromFare,
  getDepartureLegFromFare,
  useFlightFares,
} from "@/features/flights/components/flight-fares/useFlightFares";
import { FlightDetailsCard } from "@/features/flights/components/FlightDetailsCard/FlightDetailsCard";
import { useSelectedDepartureFlight } from "@/features/flights/hooks/flightSearch/useSelectedDepartureFlight";
import type { FlightType } from "@/features/flights/types/types";

type Props = {
  flight?: FlightType | null;
  onClose: () => void;
};

export const FlightDetailsDrawer = ({ flight, onClose }: Props) => {
  const intl = useIntl();
  const { selectedFare } = useFlightFares();
  const { result: departureFlight } = useSelectedDepartureFlight();
  const flightDirection =
    flight?.key === departureFlight?.key ? "departure" : "return";

  const getFareLeg =
    flightDirection === "departure"
      ? getDepartureLegFromFare
      : getArrivalLegFromFare;

  const fare = useMemo(() => {
    const fareLeg = getFareLeg(selectedFare);

    if (!fareLeg) {
      return null;
    }

    return extractDataFromFlightFare(fareLeg);
  }, [getFareLeg, selectedFare]);

  return (
    <SwipeableDrawer
      hasFullHeight
      heightOffset={24}
      open={flight !== null}
      onClose={onClose}
      backgroundColor="var(--color-surface-background)"
      fixedHeader={
        <DrawerTopBar title="Flight details" onCloseClick={onClose} />
      }
    >
      {flight ? (
        <FlightDetailsCard flight={flight} isInteractive={false} />
      ) : null}

      {fare ? (
        <div className="section-card">
          <div className="mb-4 text-lg font-medium text-text-primary">
            {intl.formatMessage({
              defaultMessage: "Fare conditions",
              description:
                "Title of branded fare section in flight order summary",
            })}
          </div>

          <div className="flex flex-col gap-4">
            {!!fare.baggage && <FlightFareBaggageInfo baggage={fare.baggage} />}

            {(!!fare.flightChange || !!fare.flightRefund) && (
              <FlightFareChangeRefundInfo
                changeInfo={fare.flightChange}
                refundInfo={fare.flightRefund}
              />
            )}

            {(!!fare.loyaltyProgram ||
              !!fare.priority ||
              !!fare.loungeAccess) && (
              <FlightFareOtherBenefitsInfo
                loyaltyProgram={fare.loyaltyProgram}
                priority={fare.priority}
                loungeAccess={fare.loungeAccess}
              />
            )}
          </div>
        </div>
      ) : null}
    </SwipeableDrawer>
  );
};
